import { useTranslation } from '../../contexts/TranslationContext';
import type { KnowledgeLawCountry } from '../../services/knowledgeLawCountries';
import { formatBillingCreditCount, replaceBillingTranslationValues } from '../../utils/billingPricing';
import { formatLandingPricingFeatureLabel, getLandingPricingFeatures, type LandingPricingPlan } from '../../utils/landingPricingPlans';
import { regulationCountryFlag, regulationCountryName } from '../../utils/regulationCountries';
import { renderHighlightedTranslation } from '../../utils/renderHighlightedTranslation';
import { Check, X } from '../Icons';
import { InfoTooltipButton } from '../InfoTooltipButton';

export function PricingFeatureList({ plan, countries, freeCredits, includedOnly = false, compact = false }: {
  plan: 'standard' | LandingPricingPlan;
  countries: KnowledgeLawCountry[];
  freeCredits?: number;
  includedOnly?: boolean;
  compact?: boolean;
}) {
  const { t, language } = useTranslation();
  const numberFormatter = new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : 'en-US');
  const usageClassName = `${compact ? 'mt-0.5' : 'mt-1'} block text-xs font-normal leading-5 text-slate-400 sm:text-sm`;
  return (
    <>
      <ul className={`mt-3 w-full max-w-md ${compact ? 'space-y-1' : 'space-y-1.5 sm:space-y-2'}`}>
        {getLandingPricingFeatures(plan === 'standard' ? 'pro' : plan, language, freeCredits)
          .filter((feature) => (!includedOnly || feature.included) && (!compact || !['monthlyCredits', 'supportFrenchTech'].includes(feature.key))).map((feature) => {
          const included = feature.included;
          const Icon = included ? Check : X;
          const labelKey = feature.labelKey;
          const estimate = feature.analysisEstimate;
          const estimateTooltip = estimate && replaceBillingTranslationValues(t(`billing.landing.${estimate.tooltipKey}`), {
            credits: numberFormatter.format(estimate.monthlyCredits),
          });
          return (
            <li
              key={feature.key}
              data-feature={feature.key}
              data-included={included}
              className={`flex items-start text-left text-[13px] leading-5 text-slate-100 ${compact ? 'gap-2' : `gap-3 sm:text-sm ${feature.key === 'transportAgent' || feature.key === 'adrAgent' ? 'md:min-h-16' : 'md:min-h-10'}`}`}
            >
              <span className={`mt-0.5 flex shrink-0 items-center justify-center ${compact ? 'h-5 w-5' : 'h-7 w-7'} ${included ? 'text-emerald-400' : 'theme-danger-icon'}`} aria-hidden="true">
                <Icon className={compact ? 'h-4 w-4' : 'h-5 w-5'} strokeWidth={3} />
              </span>
              <span className="sr-only">{t(`billing.landing.${included ? 'included' : 'notIncluded'}`)} : </span>
              <span className={`min-w-0 flex-1 ${included ? '' : 'opacity-60'}`}>
                <span className={`block ${included ? 'font-medium' : ''} ${labelKey === 'unlimitedCredits' ? 'brand-text-strong' : ''}`}>
                  {renderHighlightedTranslation({
                    text: formatLandingPricingFeatureLabel(feature, feature.key === 'monthlyCredits'
                      ? t(`billing.landing.${labelKey}`).replace('{credits}', '<strong>{credits}</strong>')
                      : t(`billing.landing.${labelKey}`), language),
                    tagName: 'strong', className: 'brand-text-strong font-bold',
                  })}
                  {feature.key === 'supportFrenchTech' && <span className="ml-1" role="img" aria-label={regulationCountryName('FRA', language)}>{regulationCountryFlag('FRA')}</span>}
                </span>
                {!compact && feature.key === 'currentRegulations' && (
                  <span className="mt-1 block">
                    {countries.map((country) => (
                      <span key={country.code} className="mr-2" role="img" aria-label={regulationCountryName(country.code, language)} title={`${regulationCountryName(country.code, language)} ${country.latestYear}`}>
                        {regulationCountryFlag(country.code)}
                      </span>
                    ))}
                  </span>
                )}
                {feature.unlimitedUsage && (
                  <span data-pricing-usage className={usageClassName}>{t('billing.landing.unlimited')}</span>
                )}
                {estimate && (
                  <>
                  {compact && <span className="mt-1 block text-sm font-semibold leading-5">
                    {renderHighlightedTranslation({
                      text: replaceBillingTranslationValues(t('billing.landing.monthlyCreditCount'), { credits: formatBillingCreditCount(estimate.monthlyCredits, language, true) }),
                      tagName: 'strong', className: 'brand-text-strong font-bold',
                    })}
                  </span>}
                  <span data-pricing-usage className={usageClassName}>
                    {replaceBillingTranslationValues(t(`billing.landing.${feature.key === 'adrAgent' ? 'consultantMonthlyEstimate' : 'agentMonthlyEstimate'}`), {
                      min: numberFormatter.format(estimate.range.min),
                      max: numberFormatter.format(estimate.range.max),
                    })}
                  </span>
                  </>
                )}
              </span>
              {!compact && 'tooltip' in feature && (
                <span className="shrink-0">
                  <InfoTooltipButton label={estimateTooltip
                    ? `${t(`billing.landing.${feature.tooltip}`)}\n${estimateTooltip}`
                    : t(`billing.landing.${feature.tooltip}`)} variant="plain" iconClassName="h-3.5 w-3.5" />
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {plan === 'enterprise' && (
        <p className="mt-3 w-full max-w-md text-left text-xs leading-5 text-slate-400">{t('billing.landing.fairUse')}</p>
      )}
    </>
  );
}
